// src/BottomBar.js
import React from "react";
import { FaHome, FaTicketAlt, FaTrophy, FaUsers } from "react-icons/fa";
import { GiCrossedSwords } from "react-icons/gi";
import { useLanguage } from "../i18n/LanguageProvider";


export default function BottomBar({
  activeTab,
  setActiveTab,
  setHomeSection,
  setView,
  hidden = false,
}) {
  const { t } = useLanguage();
  const items = [
    {
      id: "tournaments",
      label: t("bottomBar.tournaments"),
      Icon: FaTrophy,
      onClick: () => {
        setActiveTab("tournaments");
        setHomeSection("TOURNAMENTS");
        setView("LIST");
      },
    },
    {
      id: "singles",
      label: t("bottomBar.singleGames"),
      Icon: GiCrossedSwords,
      onClick: () => {
        setActiveTab("singles");
        setHomeSection("SINGLES");
        setView("LIST");
      },
    },
    {
      id: "home",
      label: t("bottomBar.home"),
      Icon: FaHome,
      onClick: () => {
        setActiveTab("home");
        setHomeSection?.(null);
        setView?.("LIST");
      },
    },
    {
      id: "vouchers",
      label: t("bottomBar.vouchers"),
      Icon: FaTicketAlt,
      onClick: () => {
        setActiveTab("vouchers");
        setHomeSection("VOUCHERS");
      },
    },
    {
      id: "community",
      label: t("bottomBar.community"),
      Icon: FaUsers,
      onClick: () => {
        setActiveTab("community");
        setHomeSection("COMMUNITY");
        setView?.("LIST");
      },
    },
  ];

return (
  <div
    style={{
      position: "fixed",
      bottom: "max(12px, env(safe-area-inset-bottom))",
      left: "calc(22px + env(safe-area-inset-left))",
      right: "calc(22px + env(safe-area-inset-right))",
      margin: "0 auto",
      transform: hidden
        ? "translate3d(0, calc(100% + 28px), 0)"
        : "translate3d(0, 0, 0)",
      width: "auto",
      maxWidth: "360px",
      boxSizing: "border-box",
      zIndex: 9999,
      opacity: hidden ? 0 : 1,
      pointerEvents: hidden ? "none" : "auto",
      transition: "transform 300ms cubic-bezier(.2,.9,.2,1), opacity 220ms ease",
    }}
    aria-hidden={hidden}
  >
    <style>
      {`
        .vq-bottom-tab:active {
          transform: translateY(0) scale(0.96) !important;
        }

        @media (hover: hover) and (pointer: fine) {
          .vq-bottom-tab:hover {
            background: rgba(255,255,255,0.10) !important;
            border-color: rgba(255,255,255,0.18) !important;
          }

          .vq-bottom-tab-active:hover {
            background: linear-gradient(135deg, rgba(255,0,127,0.96) 0%, rgba(122,43,255,0.98) 100%) !important;
            border-color: rgba(255,255,255,0.42) !important;
          }
        }

        @media (max-width: 360px) {
          .vq-bottom-tab-label {
            font-size: 7px !important;
          }
        }
      `}
    </style>
     <div
  style={{
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 4,
    padding: "7px 8px",
    borderRadius: "24px",
    border: "1px solid rgba(255,255,255,0.16)",
    background:
      "linear-gradient(135deg, rgba(16,8,58,0.94) 0%, rgba(31,13,86,0.94) 48%, rgba(14,5,64,0.94) 100%)",
    boxShadow:
      "0 18px 42px rgba(0,0,0,0.42), 0 0 26px rgba(122,43,255,0.22), inset 0 1px 0 rgba(255,255,255,0.14)",
    backdropFilter: "blur(18px)",
    WebkitBackdropFilter: "blur(18px)",
    color: "white",
    boxSizing: "border-box",
    width: "100%",
    minHeight: 62,
    overflow: "hidden",
    position: "relative",
  }}
>
        <div
          style={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            background:
              "radial-gradient(circle at 50% 0%, rgba(255,0,127,0.20), transparent 44%)",
          }}
        />
        {items.map(({ id, label, Icon, onClick }) => {
          const active = activeTab === id;
          return (
            <button
              key={id}
              type="button"
              onClick={onClick}
              className={`vq-bottom-tab${active ? " vq-bottom-tab-active" : ""}`}
              style={btnStyle(active)}
              aria-current={active ? "page" : undefined}
            >
              <span style={iconWrapStyle(active)}>
                <Icon aria-hidden="true" />
              </span>
              <span className="vq-bottom-tab-label" style={labelStyle(active, label)}>{label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function btnStyle(active) {
  return {
    flex: 1,
    minWidth: 0,
    minHeight: 48,
    textAlign: "center",
    transform: active ? "translateY(-2px)" : "translateY(0)",
    transition:
      "transform 220ms cubic-bezier(.2,.9,.2,1), background 220ms ease, box-shadow 220ms ease, color 220ms ease",
    background: active
      ? "linear-gradient(135deg, rgba(255,0,127,0.92) 0%, rgba(122,43,255,0.94) 100%)"
      : "rgba(255,255,255,0.03)",
    border: active ? "1px solid rgba(255,255,255,0.36)" : "1px solid transparent",
    borderRadius: 18,
    color: "white",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 3,
    fontFamily: "'Cooper Hewitt', system-ui, -apple-system, Arial, sans-serif",
    textTransform: "uppercase",
    cursor: "pointer",
    padding: "6px 3px",
    boxSizing: "border-box",
    boxShadow: active
      ? "0 9px 22px rgba(255,0,127,0.25), 0 0 18px rgba(122,43,255,0.24), inset 0 1px 0 rgba(255,255,255,0.22)"
      : "none",
    position: "relative",
  };
}

function iconWrapStyle(active) {
  return {
    width: 22,
    height: 22,
    display: "grid",
    placeItems: "center",
    fontSize: active ? 15 : 14,
    lineHeight: 1,
    opacity: active ? 1 : 0.74,
    transform: active ? "scale(1.04)" : "scale(1)",
    transition: "transform 220ms ease, opacity 220ms ease, font-size 220ms ease",
  };
}

function labelStyle(active, label = "") {
  const isLongLabel = String(label).length >= 10;
  const fontSize = isLongLabel
    ? active
      ? "clamp(6.4px, 1.8vw, 7.3px)"
      : "clamp(6.2px, 1.7vw, 7px)"
    : active
      ? 8.5
      : 7.5;

  return {
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "clip",
    whiteSpace: "nowrap",
    fontSize,
    fontWeight: 1000,
    lineHeight: 1,
    letterSpacing: isLongLabel ? -0.25 : 0,
    opacity: active ? 1 : 0.62,
    transition: "opacity 220ms ease, font-size 220ms ease",
  };
}
